import { CrawlRun } from '@import-io/types';
import { useParams } from '@tanstack/react-router';
import { IComment } from '_/common/types/comments.types';
import {
  useSnapshot,
  useSnapshotComments,
  useSnapshotStateTransitions,
  useSnapshotDestinationPushes,
  useSnapshotImportStatus,
  useSnapshotCrawlRun,
  useSnapshotHistory,
} from '_/hooks/useSnapshots';
import { IDestinationPush } from '_/types/destinations/destinations.types';
import { ISnapshot } from '_/types/snapshots/snapshot.types';
import React, { createContext, useContext, ReactNode } from 'react';

interface SnapshotContextValue {
  snapshotId: string | undefined;
  selectedSnapshot: ISnapshot;
  snapshotComments: IComment[];
  stateTransitions: any[];
  destinationPushes: IDestinationPush[];
  importStatus: any[];
  crawlRun: CrawlRun | null;
  snapshotHistory: any;
  isLoading: boolean;
  isLoadingComments: boolean;
  isLoadingStateTransitions: boolean;
  isLoadingDestinationPushes: boolean;
  isLoadingImportStatus: boolean;
  isLoadingCrawlRun: boolean;
  isLoadingHistory: boolean;
  isRefetching: boolean;
  refetchAll: () => void;
  refetchSnapshot: () => void;
  refetchComments: () => void;
  refetchStateTransitions: () => void;
  refetchDestinationPushes: () => void;
  refetchImportStatus: () => void;
  refetchCrawlRun: () => void;
  refetchHistory: () => void;
}

const SnapshotContext = createContext<SnapshotContextValue | undefined>(undefined);

export function SnapshotProvider({ children }: { children: ReactNode }) {
  const params = useParams({ strict: false }) as Partial<{ snapshotId?: string }>;
  const snapshotId = params.snapshotId || '';

  const { data: selectedSnapshot, isLoading, isRefetching, refetch: refetchSnapshot } = useSnapshot(snapshotId);
  const { data: snapshotComments, isLoading: isLoadingComments, refetch: refetchComments } = useSnapshotComments(snapshotId);
  const {
    data: stateTransitions,
    isLoading: isLoadingStateTransitions,
    refetch: refetchStateTransitions,
  } = useSnapshotStateTransitions(snapshotId);
  const {
    data: destinationPushes,
    isLoading: isLoadingDestinationPushes,
    refetch: refetchDestinationPushes,
  } = useSnapshotDestinationPushes(snapshotId);
  const {
    data: importStatus,
    isLoading: isLoadingImportStatus,
    refetch: refetchImportStatus,
  } = useSnapshotImportStatus(snapshotId);
  const { data: crawlRun, isLoading: isLoadingCrawlRun, refetch: refetchCrawlRun } = useSnapshotCrawlRun(snapshotId);
  const { data: snapshotHistory, isLoading: isLoadingHistory, refetch: refetchHistory } = useSnapshotHistory(snapshotId);

  const refetchAll = () => {
    void refetchSnapshot();
    void refetchComments();
    void refetchStateTransitions();
    void refetchDestinationPushes();
    void refetchImportStatus();
    void refetchCrawlRun();
    void refetchHistory();
  };

  const value: SnapshotContextValue = {
    snapshotId: snapshotId,
    selectedSnapshot: selectedSnapshot || null,
    snapshotComments: snapshotComments || [],
    stateTransitions: stateTransitions || [],
    destinationPushes: destinationPushes || [],
    importStatus: importStatus || [],
    crawlRun: crawlRun || null,
    snapshotHistory: snapshotHistory || null,
    isLoading: isLoading,
    isLoadingComments: isLoadingComments,
    isLoadingStateTransitions: isLoadingStateTransitions,
    isLoadingDestinationPushes: isLoadingDestinationPushes,
    isLoadingImportStatus: isLoadingImportStatus,
    isLoadingCrawlRun: isLoadingCrawlRun,
    isLoadingHistory: isLoadingHistory,
    isRefetching: isRefetching,
    refetchAll: refetchAll,
    refetchSnapshot: refetchSnapshot,
    refetchComments: refetchComments,
    refetchStateTransitions: refetchStateTransitions,
    refetchDestinationPushes: refetchDestinationPushes,
    refetchImportStatus: refetchImportStatus,
    refetchCrawlRun: refetchCrawlRun,
    refetchHistory: refetchHistory,
  };

  return <SnapshotContext.Provider value={value}>{children}</SnapshotContext.Provider>;
}

export function useSnapshotContext(): SnapshotContextValue {
  const context = useContext(SnapshotContext);
  if (context === undefined) {
    throw new Error('useSnapshotContext must be used within a SnapshotProvider');
  }
  return context;
}
